import React from 'react';
import { Paper, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSimulcast } from 'hooks/useSimulcast';
import {
  LAYER_H,
  LAYER_L,
  LAYER_M,
  SimulcastLayer,
  useObservedSimulcastLayers,
} from 'hooks/useObservedSimulcastLayers';

type SimulcastControlsProps = {
  mid: string;
};

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  '&:not(:first-of-type)': {
    marginTop: -1,
  },
  '& .MuiToggleButtonGroup-grouped': {
    borderRadius: 0,
    minWidth: 30,
    fontSize: 12,
    fontWeight: 500,
    padding: theme.spacing(1),
    color: theme.palette.text.primary,
    lineHeight: 1,
    '&.Mui-selected': {
      color: theme.palette.error.main,
    },
  },
}));

const SimulcastControls = ({ mid }: SimulcastControlsProps) => {
  const { temporal, changeTemporal, changeSubstream } = useSimulcast(mid);
  const { activeLayer, disabledLayers, probeLayer } = useObservedSimulcastLayers(mid);

  const handleSubstreamChange = (event: React.MouseEvent<HTMLElement>, value: number | null) => {
    if (value !== null) {
      probeLayer(value as SimulcastLayer);
    }
    changeSubstream(event, value as number);
  };

  return (
    <Paper
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <StyledToggleButtonGroup
        value={activeLayer ?? null}
        exclusive
        onChange={handleSubstreamChange}
        aria-label="Substream controls"
      >
        <ToggleButton value={LAYER_H} disabled={disabledLayers.has(LAYER_H)}>
          H
        </ToggleButton>
        <ToggleButton value={LAYER_M} disabled={disabledLayers.has(LAYER_M)}>
          M
        </ToggleButton>
        <ToggleButton value={LAYER_L} disabled={disabledLayers.has(LAYER_L)}>
          L
        </ToggleButton>
      </StyledToggleButtonGroup>
      <StyledToggleButtonGroup
        value={temporal}
        exclusive
        onChange={changeTemporal}
        aria-label="Temporal controls"
      >
        <ToggleButton value={2}>HT</ToggleButton>
        <ToggleButton value={1}>MT</ToggleButton>
        <ToggleButton value={0}>LT</ToggleButton>
      </StyledToggleButtonGroup>
    </Paper>
  );
};

export default SimulcastControls;
